import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import AmortizationSchedule from "@/components/investor/AmortizationSchedule";
import { usePrintAmortization } from "@/hooks/usePrintAmortization";
import { Investment } from "@/types/investment";
import { Printer, Calculator } from "lucide-react";

export function AmortizationPage() {
  const [inputs, setInputs] = useState({
    currentBalance: "",
    monthlyPayment: "",
    interestRate: "",
    remainingTerms: "",
    propertyAddress: "",
    hasBalloonPayment: false,
    balloonPaymentAmount: ""
  });

  const [showSchedule, setShowSchedule] = useState(false);
  const { printAmortizationSchedule } = usePrintAmortization();

  const handleInputChange = (field: string, value: string | boolean) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateSchedule = () => {
    // Validate required fields
    if (!inputs.currentBalance || !inputs.monthlyPayment || !inputs.interestRate || !inputs.remainingTerms) {
      alert("Please fill in all required fields");
      return;
    }

    setShowSchedule(true);
  };

  const clearForm = () => {
    setInputs({
      currentBalance: "",
      monthlyPayment: "",
      interestRate: "",
      remainingTerms: "",
      propertyAddress: "",
      hasBalloonPayment: false,
      balloonPaymentAmount: ""
    });
    setShowSchedule(false);
  };

  const handlePrint = () => {
    const investment: Investment = {
      id: "temp-schedule",
      type: "Custom Schedule",
      currentBalance: parseFloat(inputs.currentBalance),
      monthlyPayment: parseFloat(inputs.monthlyPayment),
      interestRate: parseFloat(inputs.interestRate),
      remainingTerms: parseInt(inputs.remainingTerms),
      propertyAddress: inputs.propertyAddress || "Custom Schedule",
      hasBalloonPayment: inputs.hasBalloonPayment,
      balloonPaymentAmount: inputs.hasBalloonPayment ? parseFloat(inputs.balloonPaymentAmount) || 0 : 0,
      askingPrice: parseFloat(inputs.currentBalance),
      status: "active",
      investor_yield: "0%",
      yield: 0,
      location: "Custom",
      riskLevel: "Medium",
      ltv: 0,
      propertyValue: parseFloat(inputs.currentBalance),
      minimumBid: parseFloat(inputs.currentBalance)
    };

    printAmortizationSchedule(investment);
  };

  const mockInvestment: Investment = {
    id: "temp-schedule",
    type: "Custom Schedule",
    currentBalance: parseFloat(inputs.currentBalance) || 0,
    monthlyPayment: parseFloat(inputs.monthlyPayment) || 0,
    interestRate: parseFloat(inputs.interestRate) || 0,
    remainingTerms: parseInt(inputs.remainingTerms) || 0,
    propertyAddress: inputs.propertyAddress || "Custom Schedule",
    hasBalloonPayment: inputs.hasBalloonPayment,
    balloonPaymentAmount: inputs.hasBalloonPayment ? parseFloat(inputs.balloonPaymentAmount) || 0 : 0,
    askingPrice: parseFloat(inputs.currentBalance) || 0,
    status: "active",
    investor_yield: "0%",
    yield: 0,
    location: "Custom",
    riskLevel: "Medium",
    ltv: 0,
    propertyValue: parseFloat(inputs.currentBalance) || 0,
    minimumBid: parseFloat(inputs.currentBalance) || 0
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Amortization Schedule Generator</h2>
        <p className="text-muted-foreground">Create and print custom amortization schedules</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Loan Details
            </CardTitle>
            <CardDescription>
              Enter the loan information to generate an amortization schedule
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentBalance">Current Balance *</Label>
                <Input
                  id="currentBalance"
                  type="number"
                  step="0.01"
                  placeholder="100000"
                  value={inputs.currentBalance}
                  onChange={(e) => handleInputChange("currentBalance", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="monthlyPayment">Monthly Payment *</Label>
                <Input
                  id="monthlyPayment"
                  type="number"
                  step="0.01"
                  placeholder="1000"
                  value={inputs.monthlyPayment}
                  onChange={(e) => handleInputChange("monthlyPayment", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="interestRate">Interest Rate (%) *</Label>
                <Input
                  id="interestRate"
                  type="text"
                  inputMode="decimal"
                  placeholder="8.5"
                  value={inputs.interestRate}
                  onChange={(e) => handleInputChange("interestRate", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="remainingTerms">Remaining Terms (months) *</Label>
                <Input
                  id="remainingTerms"
                  type="number"
                  placeholder="360"
                  value={inputs.remainingTerms}
                  onChange={(e) => handleInputChange("remainingTerms", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="propertyAddress">Property Address (Optional)</Label>
              <Input
                id="propertyAddress"
                placeholder="123 Main St, City, State"
                value={inputs.propertyAddress}
                onChange={(e) => handleInputChange("propertyAddress", e.target.value)}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasBalloonPayment"
                  checked={inputs.hasBalloonPayment}
                  onCheckedChange={(checked) => handleInputChange("hasBalloonPayment", !!checked)}
                />
                <Label htmlFor="hasBalloonPayment">Has Balloon Payment</Label>
              </div>

              {inputs.hasBalloonPayment && (
                <div className="space-y-2">
                  <Label htmlFor="balloonPaymentAmount">Balloon Payment Amount</Label>
                  <Input
                    id="balloonPaymentAmount"
                    type="number"
                    step="0.01"
                    placeholder="50000"
                    value={inputs.balloonPaymentAmount}
                    onChange={(e) => handleInputChange("balloonPaymentAmount", e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={generateSchedule} className="flex-1">
                Generate Schedule
              </Button>
              <Button variant="outline" onClick={clearForm}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {showSchedule && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Schedule Preview</CardTitle>
                <Button onClick={handlePrint} variant="outline" size="sm" className="flex items-center gap-2">
                  <Printer className="h-4 w-4" />
                  Print
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-auto">
                <AmortizationSchedule investment={mockInvestment} />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}